// src/components/molecules/UserProfile/Edit/ProfileForm.js

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Alert,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import useStore from "../../../../store/useStore";
import {
  updateProfileInFirestore,
  updateProfileImageInAllCollections,
  createUserProfileInFirestore,
  fetchAzureUserProfile,
  fetchUserProfileFromFirestore,
  checkUserNameAvailability,
  updateAzureUserProfileUsername,
} from "../../../../services/profileService";
import { useTheme } from "styled-components";
import UserProfile from "../../../../models/UserProfile";
import ProfileImage from "./ProfilePicture/ProfileImage";
import ProfilePictureControls from "./ProfilePicture/ProfilePictureControls";
import UserNameLabel from "../../../atoms/Profile/Edit/UserNameLabel";
import DisplayNameLabel from "../../../atoms/Profile/Edit/DisplayNameLabel";
import BioLabel from "../../../atoms/Profile/Edit/BioLabel";
import UserNameInput from "./UserNameInput";
import DisplayNameInput from "./DisplayNameInput";
import BioInput from "./BioInput";
import ActionButtonsGroup from "./ActionButtons/ActionButtonsGroup";

const eventsPathRegex = /^\/challenge\/events\/[^/]+(?:\/watch)?(?:\?.*)?$/;
const validateReturnTo = (p) =>
  typeof p === "string" && eventsPathRegex.test(p) ? p : null;

// Username rules
const USERNAME_MIN_LEN = 3;
const USERNAME_MAX_LEN = 20;

// Only allow: a-z, 0-9, underscore. No spaces, periods, hyphens, etc.
const USERNAME_ALLOWED_REGEX = /^[a-z0-9_]+$/;

function normalizeUsername(inputRaw) {
  const raw = String(inputRaw || "");

  // lowercase + trim
  let v = raw.toLowerCase().trim();

  // replace whitespace runs with underscore (so spaces never persist)
  v = v.replace(/\s+/g, "_");

  // remove anything that is not a-z / 0-9 / underscore
  v = v.replace(/[^a-z0-9_]/g, "");

  // collapse multiple underscores
  v = v.replace(/_+/g, "_");

  // (optional mild cleanup) avoid leading/trailing underscores
  v = v.replace(/^_+/, "").replace(/_+$/, "");

  // enforce max length
  if (v.length > USERNAME_MAX_LEN) v = v.slice(0, USERNAME_MAX_LEN);

  return v;
}

function getUsernameValidation(value) {
  const v = String(value || "");

  if (!v) {
    return { valid: false, message: "Username is required." };
  }

  if (v.includes("@")) {
    return { valid: false, message: "Username cannot be an email address." };
  }

  if (v.length < USERNAME_MIN_LEN) {
    return {
      valid: false,
      message: `Username must be at least ${USERNAME_MIN_LEN} characters.`,
    };
  }

  if (v.length > USERNAME_MAX_LEN) {
    return {
      valid: false,
      message: `Username must be ${USERNAME_MAX_LEN} characters or fewer.`,
    };
  }

  if (!USERNAME_ALLOWED_REGEX.test(v)) {
    return {
      valid: false,
      message: "Use only lowercase letters, numbers, and underscore.",
    };
  }

  return { valid: true, message: "" };
}

const ProfileForm = ({ returnTo = null }) => {
  const { userProfile, setUserProfile } = useStore();
  const navigate = useNavigate();
  const theme = useTheme();
  const auth = getAuth();

  const [formData, setFormData] = useState({
    userName: "",
    displayName: "",
    bio: "",
    profileImage: "",
  });

  const [loadingProfile, setLoadingProfile] = useState(false);

  // existing behavior: when username is an email, force them to pick one
  const [userNameError, setUsernameError] = useState(false);

  const [userNameDisabled, setUserNameDisabled] = useState(false);

  // new: format/length validation
  const [userNameValidationMessage, setUserNameValidationMessage] = useState("");
  const [isUserNameValid, setIsUserNameValid] = useState(true);

  const [usernameAvailability, setUsernameAvailability] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [user, setUser] = useState(null);

  // debounce timer for username checks
  const usernameTimerRef = useRef(null);

  // track latest check to avoid stale results
  const usernameCheckSeqRef = useRef(0);

  // Final resolved return target: prop -> sessionStorage -> null
  const resolvedReturnTo = useMemo(() => {
    const fromProp = validateReturnTo(returnTo);
    if (fromProp) return fromProp;

    const fromStorage = validateReturnTo(
      sessionStorage.getItem("returnToAfterAuth")
    );
    return fromStorage || null;
  }, [returnTo]);

  // Navigate helper with cleanup + fallback
  const navigateBackToCaller = () => {
    sessionStorage.removeItem("returnToAfterAuth");

    if (resolvedReturnTo) {
      navigate(resolvedReturnTo, { replace: true });
    } else {
      navigate("/challenge", { replace: true });
    }
  };

  // Listen for auth changes so we don't touch user.uid when null
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (!u) {
        setFormData({
          userName: "",
          displayName: "",
          bio: "",
          profileImage: "",
        });
        navigate("/challenge/signin", { replace: true });
      }
    });
    return () => unsub();
  }, [auth, navigate]);

  // cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (usernameTimerRef.current) clearTimeout(usernameTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const maybeFetchProfile = async () => {
      if (!user || userProfile) return;

      setLoadingProfile(true);
      try {
        const firestoreProfile = await fetchUserProfileFromFirestore(user.uid);

        if (firestoreProfile) {
          const normalizedExisting = normalizeUsername(firestoreProfile.userName || "");

          setUserProfile(firestoreProfile);
          setFormData({
            userName: firestoreProfile.userName || "",
            displayName: firestoreProfile.displayName || "",
            bio: firestoreProfile.bio || "",
            profileImage: firestoreProfile.profileImage || "",
          });

          // If username is an email, we want them to choose a unique one
          if ((firestoreProfile.userName || "").includes("@")) {
            setUsernameError(true);
            setUserNameDisabled(false);
            setUsernameAvailability(null);
          } else {
            // If stored username contains invalid characters, force an edit
            const v = getUsernameValidation(normalizedExisting);
            if (!v.valid) {
              setUsernameError(true);
              setUserNameDisabled(false);
              setUsernameAvailability(null);
            } else {
              setUsernameError(false);
              setUserNameDisabled(true);
            }
          }
        } else {
          const azureProfile = await fetchAzureUserProfile(user.email);

          const proposedUserName = azureProfile.userName || user.email;
          const newUserProfile = new UserProfile({
            uid: user.uid,
            displayName: azureProfile.fullName || user.displayName || "New User",
            email: azureProfile.emailAddress || user.email,
            userName: proposedUserName,
            bio: azureProfile.biography || "",
            profileImage: azureProfile.profilePicture || user.photoURL || "",
          });

          await createUserProfileInFirestore(
            user.uid,
            newUserProfile.toPlainObject()
          );

          setUserProfile(newUserProfile);
          setFormData({
            userName: newUserProfile.userName,
            displayName: newUserProfile.displayName,
            bio: newUserProfile.bio,
            profileImage: newUserProfile.profileImage,
          });

          if ((newUserProfile.userName || "").includes("@")) {
            setUsernameError(true);
            setUserNameDisabled(false);
            setUsernameAvailability(null);
          } else {
            const normalizedExisting = normalizeUsername(newUserProfile.userName || "");
            const v = getUsernameValidation(normalizedExisting);
            if (!v.valid) {
              setUsernameError(true);
              setUserNameDisabled(false);
              setUsernameAvailability(null);
            } else {
              setUsernameError(false);
              setUserNameDisabled(true);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoadingProfile(false);
      }
    };

    if (userProfile) {
      setFormData({
        userName: userProfile.userName || "",
        displayName: userProfile.displayName || "",
        bio: userProfile.bio || "",
        profileImage: userProfile.profileImage || "",
      });

      if ((userProfile.userName || "").includes("@")) {
        setUsernameError(true);
        setUserNameDisabled(false);
        setUsernameAvailability(null);
      } else {
        const normalizedExisting = normalizeUsername(userProfile.userName || "");
        const v = getUsernameValidation(normalizedExisting);
        if (!v.valid) {
          setUsernameError(true);
          setUserNameDisabled(false);
          setUsernameAvailability(null);
        } else {
          setUsernameError(false);
          setUserNameDisabled(true);
        }
      }
    } else {
      maybeFetchProfile();
    }
  }, [user, userProfile, setUserProfile]);

  // validate on username change
  useEffect(() => {
    const normalized = normalizeUsername(formData.userName);
    const v = getUsernameValidation(normalized);

    // If user is allowed to edit, enforce live validation messages
    if (!userNameDisabled) {
      setIsUserNameValid(v.valid);
      setUserNameValidationMessage(v.message);

      // if they typed mixed case or invalid chars, silently normalize in state
      // avoid infinite loop by only setting when changed
      if (formData.userName !== normalized) {
        setFormData((prev) => ({ ...prev, userName: normalized }));
      }
    } else {
      // locked usernames should not spam errors
      setIsUserNameValid(true);
      setUserNameValidationMessage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.userName, userNameDisabled]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const runUsernameAvailabilityCheck = (candidateUserName) => {
    // never check if disabled
    if (userNameDisabled) return;

    const normalized = normalizeUsername(candidateUserName);
    const validation = getUsernameValidation(normalized);

    // Don’t check availability for invalid names
    if (!validation.valid) {
      setUsernameAvailability(null);
      setCheckingUsername(false);
      return;
    }

    // Don’t check if unchanged from stored profile (after normalization)
    const existingNormalized = normalizeUsername(userProfile?.userName || "");
    if (existingNormalized && normalized === existingNormalized) {
      setUsernameAvailability(null);
      setCheckingUsername(false);
      return;
    }

    // Debounce + stale-result guard
    if (usernameTimerRef.current) clearTimeout(usernameTimerRef.current);

    setCheckingUsername(true);
    const seq = ++usernameCheckSeqRef.current;

    usernameTimerRef.current = setTimeout(async () => {
      try {
        const isAvailable = await checkUserNameAvailability(normalized);

        // ignore stale
        if (seq !== usernameCheckSeqRef.current) return;

        setUsernameAvailability(isAvailable ? "available" : "taken");
      } catch (error) {
        console.error("Error checking username availability:", error);
        if (seq !== usernameCheckSeqRef.current) return;
        setUsernameAvailability("error");
      } finally {
        if (seq === usernameCheckSeqRef.current) setCheckingUsername(false);
      }
    }, 500);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "userName") {
      // normalize immediately so no special chars persist
      const normalized = normalizeUsername(value);

      setFormData((prev) => ({ ...prev, userName: normalized }));

      // reset availability status when typing
      setUsernameAvailability(null);

      runUsernameAvailabilityCheck(normalized);
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateProfile = async () => {
    const current = auth.currentUser;
    if (!userProfile || !current?.uid) {
      setSnackbarMessage("You must be signed in to update your profile.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // If username is editable, enforce validation + availability before saving
    const normalizedUserName = normalizeUsername(formData.userName);
    const validation = getUsernameValidation(normalizedUserName);

    if (!userNameDisabled) {
      if (!validation.valid) {
        setSnackbarMessage(validation.message || "Please enter a valid username.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      if (usernameAvailability === "taken") {
        setSnackbarMessage("That username is already taken.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      // If they haven't checked yet (or it errored), avoid saving blindly
      const existingNormalized = normalizeUsername(userProfile.userName || "");
      const isChanged = normalizedUserName !== existingNormalized;

      if (isChanged && usernameAvailability !== "available") {
        setSnackbarMessage("Please confirm username availability before saving.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
    }

    try {
      // always save normalized username
      const nextFormData = {
        ...formData,
        userName: normalizedUserName,
      };

      if (!userNameDisabled) {
        const existingNormalized = normalizeUsername(userProfile.userName || "");
        if (normalizedUserName !== existingNormalized) {
          await updateAzureUserProfileUsername(normalizedUserName, current.email);
        }
      }

      const updatedProfile = { ...userProfile, ...nextFormData, uid: current.uid };
      await updateProfileInFirestore(current.uid, updatedProfile);
      setUserProfile(updatedProfile);
      setFormData((prev) => ({ ...prev, userName: updatedProfile.userName || "" }));

      setSnackbarMessage("Profile updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      // If username is no longer an email, lock it
      if (!(updatedProfile.userName || "").includes("@")) {
        setUserNameDisabled(true);
        setUsernameError(false);
      }

      navigateBackToCaller();
    } catch (error) {
      console.error("Error updating profile:", error);
      setSnackbarMessage("Error updating profile.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleImageUpload = async (imageUrl) => {
    const current = auth.currentUser;
    if (!userProfile || !current?.uid) return;

    setFormData((prev) => ({ ...prev, profileImage: imageUrl }));

    const updatedProfile = {
      ...userProfile,
      profileImage: imageUrl,
      uid: current.uid,
    };
    await updateProfileInFirestore(current.uid, updatedProfile);
    setUserProfile(updatedProfile);

    await updateProfileImageInAllCollections(current.uid, imageUrl);
  };

  const handleRemoveProfilePicture = async () => {
    const current = auth.currentUser;
    if (!userProfile || !current?.uid) return;

    const updatedProfile = { ...userProfile, profileImage: "", uid: current.uid };
    setFormData((prev) => ({ ...prev, profileImage: "" }));
    await updateProfileInFirestore(current.uid, updatedProfile);
    setUserProfile(updatedProfile);
    await updateProfileImageInAllCollections(current.uid, "");
  };

  const handleCancelEdit = () => {
    navigateBackToCaller();
  };

  if (loadingProfile) {
    return (
      <Box>
        <Typography variant="h6" sx={{ textAlign: "center", mt: 4 }}>
          Loading profile...
        </Typography>
      </Box>
    );
  }

  if (!user) return null;
  if (!userProfile) {
    return (
      <Box>
        <Typography variant="h6" sx={{ textAlign: "center", mt: 4 }}>
          Loading profile...
        </Typography>
      </Box>
    );
  }

  const showFormatError = !userNameDisabled && !!userNameValidationMessage;

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontFamily: "basic-sans",
          fontWeight: 700,
          fontSize: "2em",
          mb: 10,
        }}
      >
        Edit My Profile & Settings
      </Typography>

      {/* Profile Picture Section */}
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ backgroundColor: "#20112D", p: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "basic-sans",
              fontSize: "1em",
              fontWeight: "bold",
              color: theme.colors.amber,
            }}
          >
            Profile Picture
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        p={2}
        alignItems="center"
        sx={{ backgroundColor: "#2A1E38", mb: 8 }}
      >
        <Grid item xs={12} sm={3} sx={{ textAlign: "center" }}>
          <ProfileImage src={formData.profileImage} />
        </Grid>
        <Grid item xs={12} sm={9}>
          <ProfilePictureControls
            handleRemoveProfilePicture={handleRemoveProfilePicture}
            theme={theme}
            onImageUpload={handleImageUpload}
            userId={userProfile.uid}
          />
        </Grid>
      </Grid>

      {/* Name and Bio */}
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ backgroundColor: "#20112D", p: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "basic-sans",
              fontSize: "1em",
              fontWeight: "bold",
              color: theme.colors.amber,
            }}
          >
            Name and Bio
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={2} p={2} alignItems="center" sx={{ backgroundColor: "#2A1E38" }}>
        <UserNameLabel />
        <Grid item xs={12} sm={9}>
          {userNameError && (
            <Alert variant="filled" severity="error" sx={{ mb: 4 }}>
              Please create a unique User Name (not an email address).
            </Alert>
          )}

          {showFormatError && (
            <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
              {userNameValidationMessage} (Allowed: a-z, 0-9, underscore. {USERNAME_MIN_LEN}–{USERNAME_MAX_LEN} chars.)
            </Alert>
          )}

          {checkingUsername && (
            <Alert variant="filled" severity="info" sx={{ mb: 2 }}>
              Checking username availability...
            </Alert>
          )}
          {usernameAvailability === "available" && (
            <Alert
              variant="filled"
              severity="success"
              sx={{ mb: 2, backgroundColor: theme.colors.green }}
            >
              Username is available!
            </Alert>
          )}
          {usernameAvailability === "taken" && (
            <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
              Username is already taken.
            </Alert>
          )}
          {usernameAvailability === "error" && (
            <Alert variant="filled" severity="warning" sx={{ mb: 2 }}>
              Error checking username. Please try again later.
            </Alert>
          )}

          <UserNameInput
            theme={theme}
            userName={formData.userName}
            disabled={userNameDisabled}
            handleChange={handleChange}
            maxLength={USERNAME_MAX_LEN}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} p={2} alignItems="center" sx={{ backgroundColor: "#2A1E38" }}>
        <DisplayNameLabel />
        <Grid item xs={12} sm={9}>
          <DisplayNameInput
            displayName={formData.displayName}
            handleChange={handleChange}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} p={2} alignItems="center" sx={{ backgroundColor: "#2A1E38", mb: 8 }}>
        <BioLabel />
        <Grid item xs={12} sm={9}>
          <BioInput bio={formData.bio} handleChange={handleChange} />
        </Grid>
      </Grid>

      <ActionButtonsGroup
        handleCancel={handleCancelEdit}
        handleSave={handleUpdateProfile}
        // If your ActionButtonsGroup supports disabling Save, this is a safe UX enhancement:
        // disableSave={!userNameDisabled && (!isUserNameValid || usernameAvailability === "taken" || checkingUsername)}
      />

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <MuiAlert
          onClose={handleSnackbarClose}
          variant="filled"
          severity={snackbarSeverity}
          sx={{ width: "100%", backgroundColor: theme.colors.green }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default ProfileForm;
